import React from 'react';
import { Box, Typography, IconButton, Tooltip, Switch, Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

const SessionInfoBox = ({
  sessionReallyExpired,
  autoDownload,
  setAutoDownload,
  sessionInfoOpen,
  setSessionInfoOpen,
  jwtToken,
  expiryString,
  sessionUuid,
  setShowDownloads,
  showRenewWarning,
  setShowRenewWarning,
  BACKEND_URL,
  Cookies,
  SESSION_COOKIE,
  JWT_COOKIE,
  windowReload,
  handleRenewClick,
}) => (
  <Box
    sx={{
      position: { xs: 'static', md: 'absolute' },
      top: { md: 16 },
      right: { md: 16 },
      zIndex: 2000,
      textAlign: 'right',
      maxWidth: { xs: '100%', md: 350 },
      width: { xs: '100%', md: 'auto' },
      mb: { xs: 2, md: 0 },
    }}
  >
    {/* Session Status Indicator + Arrow */}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, justifyContent: 'flex-end' }}>
      <Box
        sx={{
          width: 14,
          height: 14,
          borderRadius: '50%',
          bgcolor: sessionReallyExpired ? '#FF1E1E' : '#00C853',
          border: '2px solid #222',
          boxShadow: sessionReallyExpired ? '0 0 6px 2px #FF1E1E55' : '0 0 6px 2px #00C85355',
          mr: 1
        }}
      />
      <Typography variant="body2" sx={{ color: sessionReallyExpired ? '#FF1E1E' : '#00C853', fontWeight: 700 }}>
        {sessionReallyExpired ? 'Expired Session' : 'Active Session'}
      </Typography>
      <IconButton size="small" onClick={() => setSessionInfoOpen(v => !v)} sx={{ ml: 1 }}>
        {sessionInfoOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
      </IconButton>
    </Box>
    {sessionInfoOpen && (
      <>
        {/* Auto-download toggle */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, justifyContent: 'flex-end' }}>
          <Typography variant="body2" sx={{ color: autoDownload ? '#FFD93D' : '#b0c4de', fontWeight: 500 }}>
            Auto-download all laps when session ends
          </Typography>
          <Switch
            checked={autoDownload}
            onChange={e => setAutoDownload(e.target.checked)}
            color="primary"
            size="small"
            inputProps={{ 'aria-label': 'auto-download laps toggle' }}
          />
        </Box>
        {/* Session Info Box: JWT, Session ID, Copy, Download Agent, Renew */}
        <Typography variant="caption" sx={{ fontWeight: 700, color: '#fff', background: 'rgba(0,0,0,0.7)', px: 1, borderRadius: 1, mb: 0.5, display: 'inline-block' }}>
          JWT Token
        </Typography>
        <Tooltip title="Copy JWT Token">
          <IconButton size="small" onClick={() => navigator.clipboard.writeText(jwtToken)}>
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Typography variant="caption" sx={{ wordBreak: 'break-all', color: '#b0c4de', background: 'rgba(0,0,0,0.5)', px: 1, borderRadius: 1, display: 'inline-block' }}>
          {jwtToken ? jwtToken.slice(0, 16) + '...' : 'No JWT'}
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', color: '#b0c4de', background: 'rgba(0,0,0,0.5)', px: 1, borderRadius: 1 }}>
          {expiryString ? `Expires: ${expiryString}` : 'No expiry'}
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', color: '#b0c4de', background: 'rgba(0,0,0,0.5)', px: 1, borderRadius: 1, mt: 0.5 }}>
          {sessionUuid ? `Session ID: ${sessionUuid}` : 'No session ID'}
        </Typography>
        {/* Download Agent and Renew buttons below session info */}
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1 }}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<CloudDownloadIcon />}
            onClick={() => setShowDownloads(true)}
          >
            Download Agent
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<RestartAltIcon />}
            onClick={handleRenewClick}
          >
            Renew
          </Button>
        </Box>
      </>
    )}
    <Dialog open={showRenewWarning} onClose={() => { setShowRenewWarning(false); }}>
      <DialogTitle>Warning: End Current Session?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Your current session is still active. If you renew now, your session will be killed and <b>all your data will be lost</b>.<br/><br/>
          Are you sure you want to continue?
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => { setShowRenewWarning(false); }} color="secondary">Cancel</Button>
        <Button color="primary" onClick={windowReload} autoFocus>
          Yes, Renew Anyway
        </Button>
      </DialogActions>
    </Dialog>
  </Box>
);

export default SessionInfoBox;
